// Client API centralisé — toute la logique HTTP passe par ici.
// Restructuration future du backend (routers par domaine) n'impose aucun
// changement ici tant que les chemins /v1/... et les formats restent stables.
//
// Mode hors-ligne (lib/offline.ts, lib/sync.ts) : une lecture (GET) qui
// échoue par manque de réseau retombe sur la dernière réponse mise en
// cache ; une écriture (POST) explicitement marquée `offlineQueue` est, à
// défaut de réseau, mise en file d'attente locale au lieu d'échouer — elle
// sera rejouée automatiquement au retour de la connexion. Les autres
// appels (login, configuration, facturation...) échouent normalement hors
// ligne : ils ne sont ni sûrs à rejouer en différé, ni nécessaires sur le
// terrain sans réseau.
import { cacheGet, cacheSet, queueWrite } from './offline';

export class ApiError extends Error {}

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  raw?: boolean; // true = body déjà sous forme binaire/texte prêt à envoyer (ex. photo)
  offlineQueue?: boolean; // écriture idempotente (porte un `ref`) : à mettre en file si hors-ligne plutôt que d'échouer
  queueKind?: string;      // catégorie affichée dans la page Synchronisation ('reception', 'issue', 'loss', 'wash_batch')
  queueLabel?: string;     // résumé lisible de cette action, affiché dans la file d'attente
}

export interface QueuedResult {
  queued: true;
  ref: string;
}

let apiBaseUrl = '';
let authToken = '';

export function configureApi(baseUrl: string, token: string) {
  apiBaseUrl = baseUrl;
  authToken = token;
}

export function uid(): string {
  if (window.crypto && 'randomUUID' in window.crypto) return crypto.randomUUID();
  return 'id-' + Date.now() + '-' + Math.random().toString(36).slice(2);
}

export async function api<T = any>(path: string, opts: ApiOptions = {}): Promise<T> {
  const method = (opts.method || 'GET').toUpperCase();
  const headers: Record<string, string> = { ...(opts.headers as Record<string, string> || {}) };
  if (!opts.raw) headers['Content-Type'] = 'application/json';
  if (authToken) headers['Authorization'] = 'Bearer ' + authToken;

  let res: Response;
  try {
    res = await fetch(apiBaseUrl + path, {
      ...opts,
      headers,
      body: opts.body !== undefined && !opts.raw ? JSON.stringify(opts.body) : (opts.body as BodyInit | undefined),
    });
  } catch {
    // Échec réseau (hors-ligne, serveur inatteignable) — distinct d'une
    // réponse HTTP d'erreur, gérée plus bas.
    return handleNetworkFailure<T>(method, path, opts);
  }

  let data: any = null;
  try { data = await res.json(); } catch { /* réponse vide ou non-JSON, ok pour certains endpoints */ }

  if (!res.ok) {
    const msg = (data && data.error && data.error.message) || `Erreur HTTP ${res.status}`;
    throw new ApiError(msg);
  }
  if (method === 'GET') cacheSet(path, data).catch(() => {});
  return data as T;
}

async function handleNetworkFailure<T>(method: string, path: string, opts: ApiOptions): Promise<T> {
  if (method === 'GET') {
    const cached = await cacheGet(path);
    if (cached) return cached.data as T;
    throw new ApiError('Hors ligne et aucune donnée en cache pour cette page.');
  }
  if (method === 'POST' && opts.offlineQueue) {
    const body = { ...(opts.body as Record<string, unknown> | undefined) };
    const ref = (body.ref as string) || uid();
    body.ref = ref;
    await queueWrite(path, body, ref, opts.queueKind || 'autre', opts.queueLabel || path);
    return { queued: true, ref } as unknown as T;
  }
  throw new ApiError('Hors ligne : impossible de contacter le serveur.');
}

export async function apiDownload(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (authToken) headers['Authorization'] = 'Bearer ' + authToken;
  const res = await fetch(apiBaseUrl + path, { headers });
  if (!res.ok) {
    let msg = `Erreur HTTP ${res.status}`;
    try { const data = await res.json(); msg = (data && data.error && data.error.message) || msg; } catch { /* ignore */ }
    throw new ApiError(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
